import DeleteConfirmationDialog from '@components/DeleteConfirmation';
import ErrorBoundary from '@components/ErrorBoundary';
import Spinner from '@components/spinner/Spinner';
import { useDeleteCode } from '@hooks/deleteQueries';
import { useFetchCodeDetails, useFetchTranscriptList } from '@hooks/getQueries';
import { useUpdateCode } from '@hooks/postQueries';
import useConfirmationDialog from '@hooks/useDeleteConfirmation';
import { Icon } from '@iconify/react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InterpreterModeOutlinedIcon from '@mui/icons-material/InterpreterModeOutlined';
import Person2OutlinedIcon from '@mui/icons-material/Person2Outlined';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormLabel,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Select,
  Stack,
  TextareaAutosize,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import formatString from '@utils/formatString';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import axiosServices from '../../../utils/axios';

const CodeQuotesModal = ({
  open,
  onClose,
  openCode,
  studyId,
  defaultTranscript,
  closeModal,
  setSelectedCode,
  setFetchThemeData,
  setOldCodesQuotesDict,
  queryKey,
  canEdit,
}) => {
  const [codesQuotesDict, setCodesQuotesDict] = useState(null);
  const { id, transcriptId } = useParams();
  const [selectedTranscript, setSelectedTranscript] = useState(transcriptId);
  const [transcriptDetail, setTranscriptDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [occurrence, setOccurrence] = useState(0);
  const [fetchingCodesQuotes, setFetchingCodesQuotes] = useState(false);
  const [codeTranscriptList, setCodeTranscriptList] = useState([]);
  const { mutateAsync: updateCode, isPending } = useUpdateCode(queryKey);
  const { open: openDeleteModal, show, handleConfirm, handleClose } = useConfirmationDialog();
  const { mutateAsync, isPending: isDeletingCode } = useDeleteCode(closeModal);
  const { data: transcripts } = useFetchTranscriptList(id);
  const [speakerList, setSpeakerList] = useState([]);
  const [isSpeakerFetching, setIsSpeakerFetching] = useState(false);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const transcriptRefs = useRef({});

  const { data: codeDetails, isFetching: isFetchingCodeDetails } = useFetchCodeDetails({
    studyId: id,
    codeName: openCode?.name,
  });

  // "See more / less" affordance for the summary. Character-length
  // heuristic (~180 chars ≈ 3 lines at the modal's typical width).
  // The previous scrollHeight/clientHeight approach raced the Dialog
  // mount transition on click-throughs from the Categories/Themes/
  // Sankey/Treemap surfaces — the useLayoutEffect measure fired while
  // the dialog was still fading in and hadn't reached its final width,
  // so scrollHeight === clientHeight and the toggle silently never
  // appeared. Length-based detection is width-independent so every
  // entry point behaves the same.
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const summaryRef = useRef(null);
  const summaryText = codeDetails?.summary;
  const summaryOverflows = (summaryText || '').length > 180;

  useEffect(() => {
    setSummaryExpanded(false);
  }, [summaryText]);

  const scrollToTurnId = (turnId) => {
    const el = transcriptRefs.current[turnId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleDeleteItem = useCallback(
    (payload) => {
      show(async () => {
        mutateAsync(payload);
      });
    },
    [mutateAsync, show],
  );

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      code: codeDetails?.code_name,
      code_definition: codeDetails?.definition,
    },
  });

  useEffect(() => {
    selectedTranscript && fetchTranscriptDetail(selectedTranscript);
  }, [selectedTranscript]);

  useEffect(() => {
    if (codeDetails) {
      setValue('code', codeDetails?.code_name);
      setValue('code_definition', codeDetails?.definition);

      if (!transcriptId || transcriptId === 'undefined') {
        setSelectedTranscript(codeDetails?.unique_transcript_ids?.[0]);
      }
    }
  }, [codeDetails, transcriptId]);

  useEffect(() => {
    if (!openCode || !defaultTranscript) return;
    const matchedCategory = Object.entries(defaultTranscript?.categories).find(([_, category]) =>
      category.codes_included.includes(openCode?.name),
    );

    if (matchedCategory) {
      setValue('code', openCode?.name || '');
      setValue('code_category', matchedCategory[0] || '');
    }
  }, [openCode, defaultTranscript]);

  useEffect(() => {
    if (selectedTranscript && codeTranscriptList) {
      fetchTranscriptDetail(selectedTranscript);
    }
  }, [selectedTranscript, codeTranscriptList]);

  useEffect(() => {
    if (selectedTranscript && codesQuotesDict) {
      let quotes = codesQuotesDict[openCode?.name];
      if (Array.isArray(quotes)) {
        const count = quotes.filter((item) => item.transcript_id === selectedTranscript).length;
        setOccurrence(count);
      } else if (quotes && typeof quotes === 'object') {
        const count = quotes?.sources?.filter(
          (item) => item.transcript_id === selectedTranscript,
        ).length;
        setOccurrence(count);
      }
    }
  }, [selectedTranscript, codesQuotesDict]);

  const fetchCodesQuotes = async () => {
    try {
      const storedId = id;
      const cachedData = localStorage.getItem('codes_quotes_dict');
      const parsedCache = cachedData ? JSON.parse(cachedData) : {};

      if (parsedCache[storedId] && Date.now() - parsedCache[storedId].timestamp < 3600000) {
        // Use cached data if available and not expired
        setCodesQuotesDict(parsedCache[storedId].data.codes_quotes_dict);
        return;
      }

      setFetchingCodesQuotes(true);
      const backendUrl = import.meta.env.VITE_BACKEND_URL;
      const url = `${backendUrl}/qualitative-studies/${storedId}/codes_quotes/`;
      const response = await axiosServices.get(url);

      let result = response.data;
      if (typeof result === 'string') {
        result = JSON.parse(result);
      }

      if (result?.codes_quotes_dict) {
        // Cache the result
        const updatedCache = {
          [storedId]: {
            data: result,
            timestamp: Date.now(),
          },
        };
        localStorage.setItem('codes_quotes_dict', JSON.stringify(updatedCache));
        setCodesQuotesDict(result.codes_quotes_dict);
      }
    } catch (error) {
      console.error('Error fetching codes quotes:', error);
    } finally {
      setFetchingCodesQuotes(false);
    }
  };
  const forcedToFetchCodesQuotes = async () => {
    try {
      setFetchingCodesQuotes(true);
      const backendUrl = import.meta.env.VITE_BACKEND_URL;
      const url = `${backendUrl}/qualitative-studies/${id}/codes_quotes/`;
      const response = await axiosServices.get(url);

      let result = response.data;
      if (typeof result === 'string') {
        result = JSON.parse(result);
        alert('yah should alert');
      }

      if (result?.codes_quotes_dict) {
        // Cache the result
        const updatedCache = {
          [id]: {
            data: result,
            timestamp: Date.now(),
          },
        };
        if (setFetchThemeData) {
          setFetchThemeData(true);
        }
        localStorage.setItem('codes_quotes_dict', JSON.stringify(updatedCache));
        if (result?.codes_quotes_dict && result?.codes_quotes_dict !== codesQuotesDict) {
          setCodesQuotesDict(result.codes_quotes_dict);
          if (setOldCodesQuotesDict) {
            setOldCodesQuotesDict(result.codes_quotes_dict);
          }
        }
      } else {
        return;
      }
    } catch (error) {
      console.error('Error fetching codes quotes:', error);
    } finally {
      setFetchingCodesQuotes(false);
    }
  };

  const handleTranscriptChange = (e) => {
    setSelectedTranscript(e.target.value);
    fetchTranscriptDetail(e.target.value);
    transcriptRefs.current = {};
    setCurrentQuoteIndex(0);
  };

  const [transcriptError, setTranscriptError] = useState(null);

  const fetchTranscriptDetail = async (transcriptId) => {
    setLoading(true);
    setTranscriptError(null);
    const backendUrl = import.meta.env.VITE_BACKEND_URL;

    try {
      const url = `${backendUrl}/qualitative-studies/${studyId}/transcripts/${transcriptId || selectedTranscript}/`;
      const response = await axiosServices.get(url);

      let transcriptData = response.data;

      if (typeof transcriptData === 'string') {
        transcriptData = JSON.parse(transcriptData);
      }
      setTranscriptDetail(transcriptData);
    } catch (error) {
      // Handle transcript not found - it may have been deleted after analysis
      setTranscriptDetail(null);
      setTranscriptError(
        'The referenced transcript is no longer available. ' +
          'The quote content is preserved in the analysis, but the full transcript context cannot be displayed.',
      );
      console.warn('Transcript not found:', error);
    } finally {
      setLoading(false);
    }
    try {
      setIsSpeakerFetching(true);
      const backendUrl = import.meta.env.VITE_BACKEND_URL;
      const url = `${backendUrl}/qualitative-studies/${studyId}/transcripts/${transcriptId || selectedTranscript}/`;
      const response = await axiosServices.get(url);

      let transcriptData = response.data;

      if (typeof transcriptData === 'string') {
        transcriptData = JSON.parse(transcriptData);
      }
      setSpeakerList(transcriptData?.speakers);
    } catch (error) {
      setIsSpeakerFetching(false);
    } finally {
      setIsSpeakerFetching(false);
    }
  };

  // Compute ``quotes`` here, before any hook references it. The
  // codes_quotes_dict shape is heterogeneous — legacy rows store an
  // array directly under the code name, newer rows store
  // ``{definition, sources}``. Either shape collapses to a flat
  // source list. Defined here (not after the early returns below) so
  // the useEffects + handleNext/handlePrevious closures don't hit a
  // temporal-dead-zone error when an early return short-circuits the
  // const declaration that used to live below.
  const rawCodeEntry = codesQuotesDict ? codesQuotesDict[openCode?.name] : null;
  const quotes = Array.isArray(rawCodeEntry) ? rawCodeEntry : rawCodeEntry?.sources || [];

  useEffect(() => {
    if (open) {
      fetchCodesQuotes();
    }
  }, [open, selectedTranscript]);

  useEffect(() => {
    if (openCode && codesQuotesDict) {
      const entry = codesQuotesDict[openCode?.name];
      if (Array.isArray(entry)) {
        const filteredTranscripts = transcripts?.transcripts?.filter((transcript) =>
          entry.some((quote) => quote.transcript_id === transcript.id),
        );

        setCodeTranscriptList(filteredTranscripts);
      } else if (entry && typeof entry === 'object') {
        const filteredTranscripts = transcripts?.transcripts?.filter((transcript) =>
          entry?.sources.some((quote) => quote.transcript_id === transcript.id),
        );
        setCodeTranscriptList(filteredTranscripts);
        setValue('code_definition', entry?.definition);
      }
    }
  }, [openCode, codesQuotesDict]);

  const getSpeakerRole = (speaker_id) => {
    let speaker = speakerList?.find((item) => item.id === speaker_id);
    return formatString(speaker?.role ?? '') || '';
  };

  const renderTextWithHighlight = (text, index) => {
    const escapeRegExp = (string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // Escapes special regex characters
    };
    if (!codesQuotesDict) return;
    let quotes = Array.isArray(codesQuotesDict[openCode?.name])
      ? codesQuotesDict[openCode?.name]
      : codesQuotesDict[openCode?.name]?.sources;
    const hoveredText = quotes?.find(
      (item) => item.transcript_id === selectedTranscript && item.turn_id === index,
    );
    let matchedText = hoveredText.text.trim();

    matchedText = escapeRegExp(matchedText);
    const parts = text?.split(new RegExp(`(${matchedText})`, 'gi')).filter(Boolean);
    return (
      <div>
        {parts.map((part, i) => {
          if (part === '') return null;
          return (
            <span
              key={i}
              style={{
                backgroundColor:
                  part.toLowerCase() === hoveredText?.text.trim()?.toLowerCase()
                    ? 'yellow'
                    : 'transparent',
                transition: 'background-color 0.3s',
              }}
            >
              {part}
            </span>
          );
        })}
      </div>
    );
  };

  const handleNext = () => {
    if (currentQuoteIndex < quotes.length - 1) {
      const nextIndex = currentQuoteIndex + 1;
      if (nextIndex < occurrence) {
        setCurrentQuoteIndex(nextIndex);
        scrollToTurnId(quotes[nextIndex].turn_id);
      }
    }
  };

  const handlePrevious = () => {
    if (currentQuoteIndex > 0) {
      const prevIndex = currentQuoteIndex - 1;
      setCurrentQuoteIndex(prevIndex);
      scrollToTurnId(quotes[prevIndex].turn_id);
    }
  };
  useEffect(() => {
    if (transcriptDetail && quotes.length > 0) {
      const firstMatchIndex = quotes.findIndex((q) => q.transcript_id === selectedTranscript);

      if (firstMatchIndex !== -1) {
        setCurrentQuoteIndex(0);
        scrollToTurnId(quotes[firstMatchIndex].turn_id);
      }
    }
  }, [transcriptDetail, selectedTranscript]);

  const handleCodeUpdate = async (data) => {
    let payload = {
      study_id: id,
      old_code: openCode?.name,
      new_code: data.code,
      definition: data.code_definition,
    };

    try {
      await updateCode({
        transcriptId: selectedTranscript,
        payload,
      });
      setSelectedCode({
        name: data.code,
        definition: data.code_definition,
        categoryKey: openCode.categoryKey,
      });
      forcedToFetchCodesQuotes();
    } catch (error) {
      console.error('Error updating code:', error);
    }
  };

  if (open && fetchingCodesQuotes) {
    return (
      <Box
        sx={{
          padding: 4,
          backgroundColor: '#fff',
          borderRadius: 2,
          maxWidth: 800,
          margin: 'auto',
          textAlign: 'center',
        }}
      >
        <CircularProgress />
        <Typography mt={2}>Loading Code Quotes...</Typography>
      </Box>
    );
  }

  if (!codesQuotesDict || !codesQuotesDict[openCode?.name]) {
    return <Box></Box>;
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} size="xl" maxWidth="800px">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <DialogTitle>{canEdit ? 'Code Details' : 'Code Details (view access only)'}</DialogTitle>
          {canEdit ? (
            <Box display={'flex'} bgcl gap={1.5} justifyContent={'end'} marginRight={'20px'}>
              <Button variant="custom" sx={{ ml: 'auto', cursor: 'pointer' }} onClick={closeModal}>
                Cancel
              </Button>
              <Button
                disabled={isDeletingCode}
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  handleDeleteItem({
                    study_id: id,
                    qualitative_item_id: selectedTranscript,
                    code: openCode?.name,
                  });
                }}
                variant="contained"
                style={{ backgroundColor: '#ff1845' }}
              >
                {isDeletingCode ? 'Deleting' : 'Delete'}
              </Button>
              <Button
                disabled={isPending}
                sx={{ cursor: 'pointer' }}
                type="submit"
                form="myForm"
                variant="contained"
              >
                {isPending ? 'Saving' : 'Save Changes'}
              </Button>
            </Box>
          ) : (
            <Box display={'flex'} justifyContent={'end'} marginRight={'20px'}>
              <Button variant="custom" sx={{ ml: 'auto', cursor: 'pointer' }} onClick={closeModal}>
                Close
              </Button>
            </Box>
          )}
        </Box>

        <Divider />
        <DialogContent
          sx={{
            maxWidth: '90vw',
            minWidth: '600px',
            position: 'relative',
            padding: '0',
          }}
        >
          {isFetchingCodeDetails ? (
            <Spinner />
          ) : (
            <ErrorBoundary>
              <Suspense fallback={<Spinner />}>
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ width: '50%' }}>
                    <Box
                      p={2}
                      sx={{
                        top: '0',
                        zIndex: '1',
                        backgroundColor: '#fff',
                        paddingY: '20px',
                      }}
                    >
                      <form id="myForm" onSubmit={handleSubmit(handleCodeUpdate)}>
                        <Stack direction="row" alignItems="center" spacing={2} fullWidth>
                          <FormLabel sx={{ minWidth: 150 }}>Code</FormLabel>
                          <Controller
                            name="code"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                              <Box
                                sx={{
                                  position: 'relative',
                                  width: '100%',
                                  '&:hover .edit-icon': {
                                    opacity: 1,
                                  },
                                }}
                              >
                                <TextField
                                  {...field}
                                  InputProps={{
                                    readOnly: !canEdit,
                                  }}
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                />
                                {canEdit && (
                                  <IconButton
                                    className="edit-icon"
                                    sx={{
                                      position: 'absolute',
                                      right: 8,
                                      top: 8,
                                      opacity: 0,
                                      transition: 'opacity 0.3s',
                                      padding: 0,
                                    }}
                                  >
                                    <Icon
                                      icon="ri:edit-fill"
                                      width="24"
                                      color="#7d5ae8"
                                      height="24"
                                    />
                                  </IconButton>
                                )}
                              </Box>
                            )}
                          />
                        </Stack>

                        <Stack direction="row" mt={1} alignItems="center" spacing={2} fullWidth>
                          <FormLabel sx={{ minWidth: 150 }}>Code Definition</FormLabel>
                          <Controller
                            name="code_definition"
                            control={control}
                            render={({ field }) => (
                              <Box
                                sx={{
                                  position: 'relative',
                                  width: '100%',
                                  '&:hover .edit-icon': {
                                    opacity: 1,
                                  },
                                }}
                              >
                                <TextareaAutosize
                                  {...field}
                                  InputProps={{
                                    readOnly: !canEdit,
                                  }}
                                  minRows={6}
                                  placeholder="Code Definition"
                                  style={{
                                    width: '100%',
                                    border: '1px solid #dfe5ef',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    fontFamily: 'inherit',
                                    fontWeight: '300',
                                    fontStyle: 'normal',
                                    color: '#333333',
                                  }}
                                />
                                {canEdit && (
                                  <IconButton
                                    className="edit-icon"
                                    sx={{
                                      position: 'absolute',
                                      right: 8,
                                      top: 8,
                                      opacity: 0,
                                      transition: 'opacity 0.3s',
                                      padding: 0,
                                    }}
                                  >
                                    <Icon
                                      icon="ri:edit-fill"
                                      width="24"
                                      color="#7d5ae8"
                                      height="24"
                                    />
                                  </IconButton>
                                )}
                              </Box>
                            )}
                          />
                        </Stack>
                        <Divider sx={{ marginTop: '10px' }} />

                        <Stack direction="row" mt={1.5} alignItems="center" spacing={2} fullWidth>
                          <FormLabel
                            sx={{
                              minWidth: 150,
                              display: 'flex',
                              gap: '10px',
                              alignItems: 'center',
                            }}
                          >
                            Saliency{' '}
                            <Tooltip
                              title="Shows the total number of times the code has been applied"
                              arrow
                            >
                              {' '}
                              <InfoOutlinedIcon sx={{ marginTop: '7px' }} />
                            </Tooltip>{' '}
                          </FormLabel>
                          <Typography>{codeDetails?.saliency}</Typography>
                        </Stack>
                        <Stack direction="row" mt={1.5} alignItems="start" spacing={2} fullWidth>
                          <FormLabel
                            sx={{
                              minWidth: 150,
                              display: 'flex',
                              gap: '10px',
                              alignItems: 'center',
                            }}
                          >
                            Summary
                          </FormLabel>
                          {/* Clamp long summaries to ~3 lines with a See more
                              toggle — matches the Codes-tab CodeDetailsModal
                              so users get consistent expand behaviour
                              regardless of which tab they clicked in from. */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {summaryText ? (
                              <>
                                <Typography
                                  ref={summaryRef}
                                  sx={
                                    summaryExpanded
                                      ? undefined
                                      : {
                                          display: '-webkit-box',
                                          WebkitLineClamp: 3,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                        }
                                  }
                                >
                                  {summaryText}
                                </Typography>
                                {summaryOverflows || summaryExpanded ? (
                                  <MuiLink
                                    component="button"
                                    type="button"
                                    variant="caption"
                                    onClick={() => setSummaryExpanded((v) => !v)}
                                    underline="hover"
                                    sx={{ mt: 0.5, fontWeight: 600 }}
                                  >
                                    {summaryExpanded ? 'See less' : 'See more'}
                                  </MuiLink>
                                ) : null}
                              </>
                            ) : (
                              <Typography color="text.disabled" variant="body2">
                                (no summary)
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </form>
                    </Box>
                  </Box>
                  <Box sx={{ width: '50%' }}>
                    {' '}
                    <Box
                      p={2}
                      mt={0}
                      sx={{
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'white',
                        zIndex: 1000,
                        boxShadow: '0px 4px 4px -2px rgba(0, 0, 0, 0.1)', // Subtle shadow at the bottom
                      }}
                    >
                      <Typography fontWeight={'bold'}>Responses</Typography>
                      <Box
                        display={'flex'}
                        alignItems={'center'}
                        gap={0.4}
                        mt={1}
                        fontSize={'13px'}
                      >
                        <InfoOutlinedIcon sx={{ color: '#3dacbf' }} /> Toggle the transcript to view
                        its corresponding interview details.
                      </Box>
                      <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                        <Stack direction="column" mt={1.5} spacing={2} width={'330px'}>
                          <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 400,
                                },
                              },
                              MenuListProps: {
                                dense: true,
                              },
                            }}
                            value={selectedTranscript || ''}
                            onChange={handleTranscriptChange}
                            displayEmpty
                          >
                            {!selectedTranscript &&
                              codeDetails?.unique_transcript_ids?.length > 0 && (
                                <MenuItem value="" disabled>
                                  Select a transcript
                                </MenuItem>
                              )}
                            {codeDetails?.unique_transcript_ids?.map((item) => (
                              <MenuItem
                                value={item}
                                key={item}
                                sx={{ maxHeight: '600px', overflowY: 'auto' }}
                              >
                                {item}
                              </MenuItem>
                            ))}
                          </Select>
                        </Stack>
                        <Box display={'flex'} alignItems={'center'} mt={4} gap={3}>
                          <Typography>
                            {`${currentQuoteIndex + 1} / ${occurrence}`}{' '}
                            {occurrence > 1 ? 'results' : 'result'} found
                          </Typography>
                          <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <Typography
                              sx={{
                                cursor: currentQuoteIndex === 0 ? 'not-allowed' : 'pointer',

                                height: '28px',
                                width: '28px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',

                                borderRadius: '24px',
                                '&:hover': {
                                  backgroundColor: '#f2f2f2',
                                },
                              }}
                              onClick={handlePrevious}
                            >
                              <ArrowBackIosIcon
                                fontSize="small"
                                sx={{ height: '12px', width: '12px', marginLeft: '5px' }}
                              />
                            </Typography>

                            <Typography
                              sx={{
                                transform: 'rotate(-180deg)',
                                cursor:
                                  currentQuoteIndex === occurrence - 1 ? 'not-allowed' : 'pointer',

                                height: '28px',
                                width: '28px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderRadius: '24px',
                                '&:hover': {
                                  backgroundColor: '#f2f2f2',
                                },
                              }}
                              onClick={handleNext}
                            >
                              <ArrowBackIosIcon
                                fontSize="small"
                                sx={{ height: '12px', width: '12px', marginLeft: '5px' }}
                              />
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    <Box height={'60vh'} sx={{ overflowY: 'auto' }}>
                      {loading ? (
                        <Spinner />
                      ) : transcriptDetail ? (
                        <Box
                          p={2}
                          sx={{
                            flexGrow: 1,
                            overflowY: 'auto',
                            borderRadius: 1,
                            marginTop: '20px',
                          }}
                        >
                          {transcriptDetail?.transcript?.map((item, index) => (
                            <Stack key={index} sx={{ mb: 2 }}>
                              <Card
                                key={index}
                                data-turn-id={index}
                                ref={(el) => {
                                  if (el) transcriptRefs.current[item.id] = el;
                                }}
                                variant="outlined"
                                sx={{
                                  backgroundColor: '#ffffff',
                                }}
                              >
                                <Box
                                  display={'flex'}
                                  alignItems={'center'}
                                  gap={'10px'}
                                  color={'#3A2A6B'}
                                  fontWeight={'bold'}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      height: '32px',
                                      width: '32px',
                                      backgroundColor: '#f3efff',
                                      borderRadius: '24px',
                                      color: '#7d5ae8',
                                      gap: '10px',
                                    }}
                                  >
                                    <Box
                                      display={'flex'}
                                      alignItems={'center'}
                                      gap={'10px'}
                                      color={'#3A2A6B'}
                                      fontWeight={'bold'}
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'center',
                                          alignItems: 'center',
                                          height: '32px',
                                          width: '32px',
                                          backgroundColor: '#7d5ae8',
                                          borderRadius: '24px',
                                          color: '#ffffff',
                                        }}
                                      >
                                        {getSpeakerRole(item?.speaker_id) === 'Participant' ? (
                                          <Person2OutlinedIcon />
                                        ) : (
                                          <InterpreterModeOutlinedIcon fontSize="small" />
                                        )}
                                      </Box>
                                    </Box>
                                  </Box>
                                  {isSpeakerFetching ? 'Loading' : getSpeakerRole(item?.speaker_id)}
                                </Box>

                                <Typography variant="body2" mt={1} mb={1}>
                                  {quotes.some(
                                    (quote) =>
                                      selectedTranscript === quote.transcript_id &&
                                      quote.turn_id === item.id,
                                  )
                                    ? quotes.map((quote) => {
                                        if (
                                          selectedTranscript === quote.transcript_id &&
                                          quote.turn_id === item.id
                                        ) {
                                          return renderTextWithHighlight(item?.text, item.id);
                                        }
                                        return null;
                                      })
                                    : item?.text}
                                </Typography>
                              </Card>
                            </Stack>
                          ))}
                        </Box>
                      ) : transcriptError ? (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '200px',
                            padding: 3,
                          }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 48, color: '#f5a623', mb: 2 }} />
                          <Typography
                            variant="body1"
                            textAlign={'center'}
                            sx={{ maxWidth: '400px', color: '#666' }}
                          >
                            {transcriptError}
                          </Typography>
                          {quotes && quotes.length > 0 && (
                            <Box
                              sx={{
                                mt: 3,
                                p: 2,
                                backgroundColor: '#f9f9f9',
                                borderRadius: 1,
                                border: '1px solid #e0e0e0',
                                maxWidth: '100%',
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Preserved Quote:
                              </Typography>
                              {quotes
                                .filter((q) => q.transcript_id === selectedTranscript)
                                .slice(0, 1)
                                .map((quote, idx) => (
                                  <Typography
                                    key={idx}
                                    variant="body2"
                                    sx={{
                                      fontStyle: 'italic',
                                      color: '#444',
                                      backgroundColor: 'yellow',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                    }}
                                  >
                                    &quot;{quote.text}&quot;
                                  </Typography>
                                ))}
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Typography mt={2} textAlign={'center'}>
                          No transcript data available.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Suspense>
            </ErrorBoundary>
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={openDeleteModal}
        onClose={handleClose}
        onConfirm={handleConfirm}
        title="Delete Code"
        isPending={isDeletingCode}
        content={`This will delete the code from the every section of the conversation. Re-run the analysis to ensure the updated data is reflected accurately..`}
        okText={isDeletingCode ? 'Deleting...' : 'Delete'}
      />
    </>
  );
};

export default CodeQuotesModal;
